import { Component, OnInit } from '@angular/core';

import { RegistererState } from 'sip.js';
import {
  combineLatest,
  firstValueFrom,
  lastValueFrom,
  map,
  Observable,
} from 'rxjs';
import { SipService } from '../services/sip.service';
import { MatDialog } from '@angular/material/dialog';
import { DeviceSettingsDialogComponent } from '../device-settings-dialog/device-settings-dialog.component';
import { CallQueueDialogComponent } from '../call-queue-dialog/call-queue-dialog.component';
import { CallQueueService } from '../services/call-queue.service';
import { TokenService } from '../services/token.service';

@Component({
  selector: 'app-status-panel',
  templateUrl: './status-panel.component.html',
  styleUrls: ['./status-panel.component.css'],
})
export class StatusPanelComponent implements OnInit {
  sipStatus$: Observable<string | null>;
  isReconnecting$ = this.sipService.isReconnecting$;

  public status$ = combineLatest([
    this.sipService.isReconnecting$,
    this.sipService.effectiveSipStatus$,
  ]).pipe(map(([reconnecting, status]) => ({ reconnecting, status })));

  constructor(
    private sipService: SipService,
    private callQueueService: CallQueueService,
    private dialog: MatDialog,
    private tokenService: TokenService // Assuming you have a token service for SIP
  ) {
    this.sipStatus$ = this.sipService.effectiveSipStatus$;

    sipService.delegate = {
      onRegistered: () => {
        console.log('✅ SIP registered (delegate)');
        // update UI or state here
      },
      onUnregistered: async () => {
        console.log('⚠️ SIP unregistered (delegate)');
        await this.sipService.register();
        // show error, prompt reconnect, etc.
      },
    };
  }

  ngOnInit(): void {}

  async onIconClick() {
    await this.sipService.register();
  }

  getStatusIcon(status: string): string {
    switch (status) {
      case 'registered':
        return '✅';
      case 'disconnected':
        return '❌';
      case 'terminated':
        return '☠️';
      case 'connecting':
        return '⏳';
      case 'offline':
        return '📴'; // No internet
      default:
        return '❓';
    }
  }

  getStateText(state: RegistererState | null): string {
    switch (state) {
      case RegistererState.Registered:
        return 'Registered';
      case RegistererState.Unregistered:
        return 'Disconnected';
      case RegistererState.Terminated:
        return 'Terminated';
      case RegistererState.Initial:
      default:
        return 'Connecting...';
    }
  }

  openDeviceSettingsDialog = async () => {
    // Placeholder for opening device settings dialog
    console.log('Open device settings dialog');

    const devices = await this.sipService.enumerateAudioDevices(); // ✅ Fetch devices on startup

    if (!devices) return;

    const microphones = [...devices.microphones];
    const speakers = [...devices.speakers];

    const storedMicId = this.sipService.getSelectedMicrophone();
    const mic = microphones.find((d) => d.deviceId === storedMicId);
    const selectedMic = mic ? mic.deviceId : microphones[0]?.deviceId; // fallback to first
    console.log('Selected microphone:', selectedMic);

    const storedSpeakerId = this.sipService.getSelectedSpeaker();
    const speaker = speakers.find((d) => d.deviceId === storedSpeakerId);
    const selectedSpeaker = speaker ? speaker.deviceId : speakers[0]?.deviceId;

    console.log('🎤 Available Microphones:', microphones);
    console.log('🔊 Available Speakers:', speakers);

    // Implement dialog logic here
    this.dialog.open(DeviceSettingsDialogComponent, {
      width: '400px',

      data: {
        microphones: devices.microphones,
        speakers: devices.speakers,
        selectedMic: selectedMic,
        selectedSpeaker: selectedSpeaker,
      },
    });
  };

  async openCallQueueDialog() {
    try {
      const data = await firstValueFrom(
        this.callQueueService.getUserCallQueueLogins()
      );
      const eveUserId =
        this.tokenService.getLoginRefreshToken().eveUser.eveUserId; // Assuming you have a method to get the Eve user name
      this.dialog.open(CallQueueDialogComponent, {
        width: '400px',
        data: { queues: data, eveUserId: eveUserId },
      });
    } catch (err) {
      console.error('Failed to fetch call queues', err);
    }
  }
}
